import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  BatchApprovalRequest,
  BatchApprovalResult,
  MonthlyReportId,
  OperatorIncidentSnapshot,
  OperatorPipelineCard,
  OperatorReportRow,
  RecapPeriod,
  SeoAuditRunId,
  SiteId,
} from "@siteagain/shared-types";

import { LOCALES, setLocale, useLocale, type Locale } from "./i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { PilotInbox } from "./pilot-inbox";
import { operatorCopy, type OperatorCopy } from "./operator/copy";
import { AuditPage } from "./operator/AuditPage";
import { failedCards, freshAudits } from "./operator/NeedsYou";
import { operatorFetch, operatorPost, relativeTime } from "./operator/format";
import { AlertIcon, CheckIcon, RefreshIcon } from "./operator/icons";
import { OnboardSiteModal } from "./operator/OnboardSiteModal";
import type { DecisionAction, RecommendationAction } from "./operator/v2/PipelinePage";
import { ReportModal, ReportsPanel } from "./operator/ReportsPanel";
import { useMissionControl, type Freshness } from "./operator/use-mission-control";
import { buildInbox } from "./operator/v2/data";
import { mc2Copy } from "./operator/v2/copy";
import { BriefPage } from "./operator/v2/BriefPage";
import { InboxPage } from "./operator/v2/InboxPage";
import { FleetPage } from "./operator/v2/FleetPage";
import { PipelinePage } from "./operator/v2/PipelinePage";
import { SitePage } from "./operator/v2/SitePage";
import { MissionShell, type MCView } from "./operator/v2/shell";
import "./operator/operator.css";
import "./operator/mission-control-v2.css";

interface OperatorUser {
  email: string;
  name: string;
}

type Tab = MCView;

const AUDIT_HASH = /^#audit\/(sea_[A-Za-z0-9_-]+)$/u;
const SEEN_AUDITS_KEY = "mc.seenAudits";
const FRESH_AUDIT_MS = 24 * 60 * 60 * 1000;

/** The audit page lives in the address, so it can be linked to and the back button closes it. */
function auditFromHash(): SeoAuditRunId | null {
  return (AUDIT_HASH.exec(window.location.hash)?.[1] as SeoAuditRunId | undefined) ?? null;
}

/** Which audits this operator already opened, so a read report stops asking for attention. Per browser, best effort. */
function readSeenAudits(): Set<string> {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(SEEN_AUDITS_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function rememberSeenAudit(id: SeoAuditRunId): void {
  try {
    const seen = [...readSeenAudits(), id].slice(-200);
    window.localStorage.setItem(SEEN_AUDITS_KEY, JSON.stringify(seen));
  } catch {
    // Private windows and blocked storage: the inbox simply keeps showing it.
  }
}

function greetingFor(now: number, copy: OperatorCopy): string {
  const hour = new Date(now).getHours();
  return hour < 12 ? copy.today.greeting.morning : hour < 18 ? copy.today.greeting.afternoon : copy.today.greeting.evening;
}

const DECISION_NOTICE = {
  approve: "approved",
  reject: "rejected",
  rollback: "rollbackStarted",
} as const satisfies Record<DecisionAction, "approved" | "rejected" | "rollbackStarted">;

const RECOMMENDATION_NOTICE = {
  preview: "previewStarted",
  save: "saved",
  skip: "dismissed",
} as const satisfies Record<RecommendationAction, "previewStarted" | "saved" | "dismissed">;

export function OperatorConsole({
  user,
  onExit,
  onSignOut,
}: {
  user: OperatorUser;
  onExit(): void;
  onSignOut(): void;
}) {
  const locale = useLocale();
  const copy = useMemo(() => operatorCopy(locale), [locale]);

  const [tab, setTab] = useState<Tab>("brief");
  const [period, setPeriod] = useState<RecapPeriod>("7d");
  const [searchQuery, setSearchQuery] = useState("");
  const [inboxId, setInboxId] = useState<string | null>(null);
  const [openSiteId, setOpenSiteId] = useState<SiteId | null>(null);
  const [openReportId, setOpenReportId] = useState<MonthlyReportId | null>(null);
  const [busyKey, setBusyKey] = useState("");
  const [incidentBusyId, setIncidentBusyId] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [version, setVersion] = useState(0);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [auditId, setAuditId] = useState<SeoAuditRunId | null>(() => auditFromHash());
  const [seenAudits, setSeenAudits] = useState<Set<string>>(() => readSeenAudits());
  /** A site just added here: its first audit opens by itself when it lands. */
  const [awaitingFirstAudit, setAwaitingFirstAudit] = useState<SiteId | null>(null);
  const pushedAudit = useRef(false);

  const mission = useMissionControl({ siteId: null, period });
  const { overview, pipeline, recap, reload, now } = mission;

  const [reportRows, setReportRows] = useState<OperatorReportRow[] | null>(null);
  const [reportsError, setReportsError] = useState("");
  const loadReports = useCallback(async () => {
    try {
      const response = await operatorFetch<{ reports: OperatorReportRow[] }>("/operator/reports");
      setReportRows(response.reports);
      setReportsError("");
    } catch (reason) {
      setReportsError(reason instanceof Error ? reason.message : copy.reports.loadFailed);
    }
  }, [copy.reports.loadFailed]);

  useEffect(() => {
    if (!["brief", "inbox", "reports"].includes(tab)) return;
    void loadReports();
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void loadReports(); }, 30_000);
    return () => window.clearInterval(timer);
  }, [tab, loadReports, version]);

  useEffect(() => {
    const sync = () => { pushedAudit.current = false; setAuditId(auditFromHash()); };
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const openAudit = useCallback((id: SeoAuditRunId) => {
    if (auditFromHash() !== id) {
      window.history.pushState(null, "", `${window.location.pathname}${window.location.search}#audit/${id}`);
      pushedAudit.current = true;
    }
    rememberSeenAudit(id);
    setSeenAudits((current) => new Set(current).add(id));
    setAuditId(id);
  }, []);

  const closeAudit = useCallback(() => {
    // Back where we came from when we put the audit in the address; otherwise just drop it.
    if (pushedAudit.current) window.history.back();
    else window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    pushedAudit.current = false;
    setAuditId(null);
  }, []);

  useEffect(() => {
    if (!awaitingFirstAudit || !overview) return;
    const site = overview.sites.find((item) => item.id === awaitingFirstAudit);
    if (!site?.latestSeoAuditId) return;
    setAwaitingFirstAudit(null);
    setNotice(copy.audit.ready(site.name));
    openAudit(site.latestSeoAuditId);
  }, [awaitingFirstAudit, overview, openAudit, copy.audit]);

  /** One path for every action: busy marker, outcome message, then refresh everything on screen. */
  async function perform(key: string, task: () => Promise<string>) {
    setBusyKey(key);
    setActionError("");
    setNotice("");
    try {
      setNotice(await task());
    } catch (reason) {
      setActionError(copy.notice.failed(reason instanceof Error ? reason.message : String(reason)));
    } finally {
      setBusyKey("");
      setVersion((value) => value + 1);
      void reload();
    }
  }

  const decide = (changeSetId: string, action: DecisionAction, key: string) =>
    perform(key, async () => {
      await operatorPost(`/operator/change-sets/${changeSetId}/${action}`);
      return copy.notice[DECISION_NOTICE[action]];
    });

  const recommend = (recommendationId: string, action: RecommendationAction, key: string) =>
    perform(key, async () => {
      await operatorPost(`/operator/recommendations/${recommendationId}/${action}`);
      return copy.notice[RECOMMENDATION_NOTICE[action]];
    });

  const onCardDecision = (card: OperatorPipelineCard, action: DecisionAction) => {
    if (card.changeSetId) void decide(card.changeSetId, action, `${card.key}:${action}`);
  };
  const onCardRecommendation = (card: OperatorPipelineCard, action: RecommendationAction) => {
    if (card.recommendationId) void recommend(card.recommendationId, action, `${card.key}:${action}`);
  };
  const onBatch = (request: BatchApprovalRequest) =>
    perform("batch", async () => {
      const result = await operatorPost<BatchApprovalResult>("/operator/change-sets/approve-batch", request);
      const approved = result.results.filter((item) => item.status === "approved").length;
      return copy.notice.batch(approved, result.results.length - approved);
    });
  const onRunAudit = (siteId: SiteId, key: string) =>
    perform(key, async () => {
      await operatorPost(`/operator/sites/${siteId}/seo-audits`);
      return copy.notice.auditRequested;
    });

  async function actOnIncident(incident: OperatorIncidentSnapshot, action: "take" | "resolve") {
    setIncidentBusyId(incident.id);
    setActionError("");
    try {
      await operatorPost(
        `/operator/queue/${incident.id}/${action === "take" ? "assign" : "resolve"}`,
        action === "take" ? { owner: user.email } : { notes: `Resolved by ${user.email} from Mission Control` },
      );
      void reload();
    } catch (reason) {
      setActionError(copy.notice.failed(reason instanceof Error ? reason.message : String(reason)));
    } finally {
      setIncidentBusyId("");
    }
  }

  const dismissAudit = useCallback(() => {
    if (!auditId) return;
    pushedAudit.current = false;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    setAuditId(null);
  }, [auditId]);
  const openSite = useCallback((siteId: SiteId) => {
    dismissAudit();
    setOpenSiteId(siteId);
  }, [dismissAudit]);
  const closeOnboard = useCallback(() => setOnboardOpen(false), []);
  const stoppedCards = useMemo(
    () => failedCards(pipeline?.columns.flatMap((column) => column.cards) ?? []),
    [pipeline],
  );
  const approveCards = useMemo(
    () => pipeline?.columns.find((column) => column.stage === "to_approve")?.cards ?? [],
    [pipeline],
  );
  const newAudits = useMemo(
    () => freshAudits(recap?.journal ?? [])
      .filter((entry) => !seenAudits.has(entry.auditRunId) && now - new Date(entry.at).getTime() < FRESH_AUDIT_MS),
    [recap, seenAudits, now],
  );

  const mc = useMemo(() => mc2Copy(locale), [locale]);

  const inboxItems = useMemo(() => {
    if (!overview) return [];
    return buildInbox({
      stopped: stoppedCards,
      toApprove: approveCards,
      audits: newAudits,
      sites: overview.sites,
      reports: reportRows ?? [],
      incidents: overview.incidents,
    });
  }, [overview, stoppedCards, approveCards, newAudits, reportRows]);

  const selectedInboxId = inboxId && inboxItems.some((item) => item.id === inboxId) ? inboxId : inboxItems[0]?.id ?? null;
  const pipelineTotal = pipeline?.columns.reduce((sum, column) => sum + column.total, 0) ?? 0;
  const draftCount = (reportRows ?? []).filter((row) => row.status === "draft").length;
  const activeWorkflows = overview?.fleet.activeWorkflows ?? 0;
  const monitoringLabel = activeWorkflows > 0
    ? `${mc.rail.monitoring} · ${mc.rail.active(activeWorkflows)}`
    : mc.rail.monitoring;
  const refreshSlot = (
    <RefreshStatus
      freshness={mission.freshness}
      lastOkAt={mission.lastOkAt}
      copy={copy}
      locale={locale}
      now={now}
      onRefresh={() => { void reload(); setVersion((value) => value + 1); }}
    />
  );

  return (
    <MissionShell
      view={openSiteId || auditId ? "fleet" : tab}
      onView={(next) => { dismissAudit(); setOpenSiteId(null); setTab(next); }}
      counts={{ inbox: inboxItems.length, fleet: overview?.sites.length ?? 0, pipeline: pipelineTotal, reports: draftCount }}
      sites={overview?.sites ?? []}
      activeWorkflows={activeWorkflows}
      monthUsageMicroUsd={overview?.fleet.monthUsageMicroUsd ?? 0}
      operatorEmail={user.email}
      copy={mc}
      locale={locale}
      onOpenSite={openSite}
      onExit={onExit}
      monitoringLabel={monitoringLabel}
      pageKey={`${tab}:${openSiteId ?? ""}:${auditId ?? ""}`}
      accountSlot={<AccountMenu user={user} locale={locale} copy={copy} onSignOut={onSignOut} />}
    >
      {mission.error && (
        <div className="mc2-banner bad" role="alert">
          <AlertIcon /><span>{mission.error}</span>
          <button type="button" aria-label={copy.action.close} onClick={mission.clearError}>×</button>
        </div>
      )}
      {actionError && (
        <div className="mc2-banner bad" role="alert">
          <AlertIcon /><span>{actionError}</span>
          <button type="button" aria-label={copy.action.close} onClick={() => setActionError("")}>×</button>
        </div>
      )}
      {notice && (
        <div className="mc2-banner notice" role="status">
          <CheckIcon /><span>{notice}</span>
          <button type="button" aria-label={copy.action.close} onClick={() => setNotice("")}>×</button>
        </div>
      )}

      {auditId ? (
        <AuditPage
          auditId={auditId}
          copy={copy}
          locale={locale}
          now={now}
          version={version}
          busyKey={busyKey}
          onBack={closeAudit}
          onOpenSite={openSite}
          onRunAudit={(siteId, key) => void onRunAudit(siteId, key)}
          onRecommendation={(id, action, key) => void recommend(id, action, key)}
        />
      ) : openSiteId ? (
        <SitePage
          siteId={openSiteId}
          copy={mc}
          ops={copy}
          locale={locale}
          now={now}
          version={version}
          busyKey={busyKey}
          onBack={() => setOpenSiteId(null)}
          onDecide={(changeSetId, action, key) => void decide(changeSetId, action, key)}
          onRecommendation={(id, action, key) => void recommend(id, action, key)}
          onRunAudit={(siteId, key) => void onRunAudit(siteId, key)}
          onOpenReport={setOpenReportId}
          onOpenAudit={openAudit}
        />
      ) : !overview ? (
        <div className="mc2-loading" role="status">{mission.freshness === "offline" ? copy.status.offline(null) : copy.status.loading}</div>
      ) : tab === "brief" ? (
        <BriefPage
          overview={overview}
          pipeline={pipeline}
          recap={recap}
          reports={reportRows}
          inbox={inboxItems}
          copy={mc}
          ops={copy}
          locale={locale}
          now={now}
          period={period}
          onPeriod={setPeriod}
          sites={overview.sites}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          onOpenSite={openSite}
          onOpenAudit={openAudit}
          onOpenReport={setOpenReportId}
          onOpenInbox={(id) => { setInboxId(id); setTab("inbox"); }}
          onView={(next) => setTab(next)}
          onAddSite={() => setOnboardOpen(true)}
          refreshSlot={refreshSlot}
        />
      ) : tab === "inbox" ? (
        <InboxPage
          items={inboxItems}
          selectedId={selectedInboxId}
          onSelect={setInboxId}
          sites={overview.sites}
          copy={mc}
          ops={copy}
          locale={locale}
          now={now}
          busyKey={busyKey}
          onOpenSite={openSite}
          onOpenAudit={openAudit}
          onOpenReport={setOpenReportId}
          onRetry={(item) => onCardRecommendation(item.card, "preview")}
          onDecide={(item, action) => onCardDecision(item.card, action)}
          onRunAudit={(siteId) => void onRunAudit(siteId, `site:${siteId}:audit`)}
          onTakeIncident={(incident) => void actOnIncident(incident, "take")}
          onResolveIncident={(incident) => void actOnIncident(incident, "resolve")}
          incidentBusyId={incidentBusyId}
        />
      ) : tab === "fleet" ? (
        <FleetPage
          overview={overview}
          recap={recap}
          reports={reportRows}
          copy={mc}
          ops={copy}
          locale={locale}
          now={now}
          onOpenSite={openSite}
          onOpenAudit={openAudit}
          onOpenReport={setOpenReportId}
          onAddSite={() => setOnboardOpen(true)}
          refreshSlot={refreshSlot}
        />
      ) : tab === "pipeline" ? (
        <PipelinePage
          pipeline={pipeline}
          copy={mc}
          ops={copy}
          locale={locale}
          now={now}
          busyKey={busyKey}
          onOpenSite={openSite}
          onRecommendation={onCardRecommendation}
          onDecide={onCardDecision}
          onBatch={(request) => void onBatch(request)}
          onAddSite={() => setOnboardOpen(true)}
          refreshSlot={refreshSlot}
        />
      ) : tab === "reports" ? (
        <>
          <div className="mc2-topbar">
            <div>
              <div className="mc2-eyebrow">{mc.reports.eyebrow}</div>
              <h1 className="mc2-serif" style={{ fontSize: 40, lineHeight: 1.05, margin: "6px 0 0" }}>{mc.reports.title}</h1>
            </div>
            <div className="mc2-topactions">{refreshSlot}</div>
          </div>
          <p className="mc2-muted" style={{ margin: "-10px 0 20px", maxWidth: 720 }}>{mc.reports.subtitle}</p>
          <ReportsPanel
            rows={reportRows}
            loadError={reportsError}
            copy={copy}
            locale={locale}
            now={now}
            scopeSiteId={null}
            onOpen={setOpenReportId}
          />
        </>
      ) : (
        <>
          <div className="mc2-topbar">
            <div>
              <div className="mc2-eyebrow">{copy.header.eyebrowPilots}</div>
              <h1 className="mc2-serif" style={{ fontSize: 40, lineHeight: 1.05, margin: "6px 0 0" }}>{copy.header.pilotsTitle}</h1>
            </div>
            <div className="mc2-topactions">{refreshSlot}</div>
          </div>
          <PilotInbox operatorEmail={user.email} />
        </>
      )}

      {openReportId && (
        <ReportModal
          reportId={openReportId}
          copy={copy}
          locale={locale}
          now={now}
          onClose={() => setOpenReportId(null)}
          onDecided={(decision) => {
            setNotice(decision === "approve" ? copy.notice.reportApproved : copy.notice.reportSuppressed);
            setVersion((value) => value + 1);
            void reload();
          }}
        />
      )}

      {onboardOpen && (
        <OnboardSiteModal
          copy={mc}
          ops={copy}
          locale={locale}
          onClose={closeOnboard}
          onCreated={(siteId) => {
            closeOnboard();
            setNotice(copy.fleet.onboardModal.successNotice);
            setAwaitingFirstAudit(siteId);
            openSite(siteId);
            void reload();
          }}
        />
      )}
    </MissionShell>
  );
}

/**
 * One control for "how fresh is this, and refresh it". The label is derived from what actually
 * loaded and when, so it can say it is out of date, and it is also the button that fixes that.
 */
function RefreshStatus({
  freshness,
  lastOkAt,
  copy,
  locale,
  now,
  onRefresh,
}: {
  freshness: Freshness;
  lastOkAt: number | null;
  copy: OperatorCopy;
  locale: Locale;
  now: number;
  onRefresh(): void;
}) {
  const ago = lastOkAt === null ? null : relativeTime(new Date(lastOkAt).toISOString(), locale, now);
  const label = {
    loading: copy.status.loading,
    updating: copy.status.updating,
    live: copy.status.live(ago ?? ""),
    stale: copy.status.stale(ago ?? ""),
    partial: copy.status.partial(ago),
    offline: copy.status.offline(ago),
  }[freshness];
  const busy = freshness === "loading" || freshness === "updating";
  return (
    <button
      type="button"
      className={`mc2-refresh state-${freshness}`}
      onClick={onRefresh}
      disabled={busy}
      title={copy.header.refreshNow}
      aria-label={`${label}. ${copy.header.refreshNow}`}
    >
      <i aria-hidden="true" />
      <span>{label}</span>
      <RefreshIcon />
    </button>
  );
}

/** The operator's own menu: who they are, the language of the screen, and signing out. */
function AccountMenu({ user, locale, copy, onSignOut }: { user: OperatorUser; locale: Locale; copy: OperatorCopy; onSignOut(): void }) {
  const names: Record<Locale, string> = { en: copy.account.english, fr: copy.account.french };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="ops-avatar" aria-label={copy.account.menu} title={user.email}>
          {(user.name || user.email).slice(0, 1).toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end">
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{copy.account.language}</DropdownMenuLabel>
        {LOCALES.map((value) => (
          <DropdownMenuItem key={value} lang={value} onSelect={() => setLocale(value)}>
            {names[value]}{locale === value && <CheckIcon />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>{copy.account.signOut}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
